/**
 * Bolt Configuration Service
 *
 * Handles the business logic for bolt configuration operations:
 * - Finding families with OBJECT-TYPE = BOLT or GASKET
 * - Loading reference families when needed
 * - Adding bolt parameters to families
 *
 * This service follows SoC by:
 * - Using plain interfaces (not store-specific types)
 * - Separating pure setup planning from host/Revit operations
 * - Having no React or UI dependencies
 */

import type { RevitFamily } from '../api/revit/types';
import {
  createBoltSetupPlan,
  type BoltConfigurationInput,
  type BoltDefaultParameterRemoval,
  type BoltFamilyParameterBindingChange,
  type BoltModelingType,
} from './boltParameterSetup/planner';
import { addParametersToBoltFamilies } from './boltParameterSetup/familyParameterHostActions';
import { loadReferenceFamilyAndReconcileParameters } from './boltParameterSetup/loadedFamilyReconciliation';
import {
  resolveBoltParameterTypeHints,
} from './boltParameterSetup/boltParameterTypes';
import {
  addProjectBoltParameters,
  type BoltConfigurationOptions,
} from './boltParameterSetup/projectParameterHostActions';
import type { ConnectorParameterBindingKind } from '../types';

export type { BoltConfigurationOptions } from './boltParameterSetup/projectParameterHostActions';

const log = (_message: string, _data?: unknown) => {};

export interface BoltConfigurationResult {
  success: boolean;
  action:
    | 'added_to_existing'
    | 'added_project_parameters'
    | 'loaded_new_family'
    | 'no_action'
    | 'error';
  message: string;
  targetObjectType: BoltModelingType;
  details?: {
    familyCount?: number;
    familyNames?: string[];
    loadedFamilyId?: string;
    loadedFamilyName?: string;
    parameterCount?: number;
    projectParameterNames?: string[];
    projectParameterCategories?: string[];
    bindingKind?: ConnectorParameterBindingKind;
    addedFamilyParameterNames?: string[];
    reboundFamilyParameters?: BoltFamilyParameterBindingChange[];
    removedDefaultParameters?: BoltDefaultParameterRemoval[];
    modifiedFamiliesCount?: number;
    resolvedFamilyTargetCount?: number;
    processedFamilyTargetCount?: number;
    openedFamilyCount?: number;
    unmodifiedFamilyTargetCount?: number;
    existingFamilyParameterCount?: number;
    conflictCount?: number;
    failureCount?: number;
  };
  undoAvailable?: boolean;
  error?: string;
}

// ============================================================================
// API Operations (Async)
// ============================================================================

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}

function buildFamilyParameterMutationMessage(
  targetObjectType: BoltModelingType,
  modifiedCount: number,
  targetCount: number,
  unmodifiedCount: number
): string {
  const targetSuffix = targetCount !== modifiedCount
    ? ` out of ${targetCount} ${targetObjectType} family definition ${pluralize(targetCount, 'target')}`
    : '';
  const unchangedSuffix = unmodifiedCount > 0
    ? `; ${unmodifiedCount} ${pluralize(unmodifiedCount, 'target')} needed no reload`
    : '';

  return `Changed and loaded ${modifiedCount} ${targetObjectType} family ${pluralize(modifiedCount, 'definition')}${targetSuffix}${unchangedSuffix}`;
}

// ============================================================================
// Main Service Function
// ============================================================================

/**
 * Apply bolt configuration
 *
 * This is the primary entry point for bolt configuration operations.
 * It determines the target object type, checks for existing families,
 * and either adds parameters to existing families or loads a reference family.
 *
 * @param configInput - Plain configuration input (not store-coupled)
 * @param families - Revit families to check
 * @returns Result with action taken and details
 */
const isFamilyTargetResolutionFailure = (error?: string): boolean =>
  /no editable loadable families matched/i.test(error ?? '');

export async function applyBoltConfiguration(
  configInput: BoltConfigurationInput,
  families: RevitFamily[],
  options: BoltConfigurationOptions = {}
): Promise<BoltConfigurationResult> {
  log(`========================================`);
  log(`=== APPLY BOLT CONFIGURATION START ===`);
  log(`========================================`);
  log(`Input configuration:`, configInput);
  log(`Families available: ${families.length}`);

  const setupPlan = createBoltSetupPlan(configInput, families);
  const { parameterNames, targetObjectType } = setupPlan;
  const parameterTypeHints = resolveBoltParameterTypeHints(configInput);

  log(`Bolt modeling choice: "${configInput.boltModelingChoice}"`);
  log(`Target object type: ${targetObjectType}`);
  log(`Bolt parameters to add: ${parameterNames.length}`, parameterNames);

  if (setupPlan.action === 'no_action' && setupPlan.reason === 'no_parameters') {
    log(`No parameters to add - aborting`);
    return {
      success: true,
      action: 'no_action',
      message: 'No bolt parameters selected',
      targetObjectType,
    };
  }

  if (setupPlan.action === 'no_action' && setupPlan.reason === 'family_parameters_available') {
    log(`All matching ${targetObjectType} families already contain the requested parameters`);
    return {
      success: true,
      action: 'no_action',
      message: `All ${targetObjectType} families already contain the requested bolt parameters`,
      targetObjectType,
      details: {
        familyCount: setupPlan.matchingFamilies.length,
        familyNames: setupPlan.matchingFamilies.map((family) => family.name),
        parameterCount: parameterNames.length,
      },
    };
  }

  if (setupPlan.action === 'add_project_parameters') {
    const result = await addProjectBoltParameters(
      parameterNames,
      setupPlan.projectParameterCategories,
      setupPlan.bindingKind,
      parameterTypeHints,
      options
    );

    if (!result.success) {
      return {
        success: false,
        action: 'error',
        message: `Found ${targetObjectType} families but failed to add project parameters`,
        targetObjectType,
        error: result.error,
        details: {
          familyCount: setupPlan.matchingFamilies.length,
          familyNames: setupPlan.matchingFamilies.map((family) => family.name),
          parameterCount: parameterNames.length,
          projectParameterNames: parameterNames,
          projectParameterCategories: setupPlan.projectParameterCategories,
          bindingKind: setupPlan.bindingKind,
        },
      };
    }

    return {
      success: true,
      action: 'added_project_parameters',
      message: `Added ${parameterNames.length} bolt project parameters to ${setupPlan.projectParameterCategories.join(', ')}`,
      targetObjectType,
      undoAvailable: true,
      details: {
        familyCount: setupPlan.matchingFamilies.length,
        familyNames: setupPlan.matchingFamilies.map((family) => family.name),
        parameterCount: parameterNames.length,
        projectParameterNames: parameterNames,
        projectParameterCategories: setupPlan.projectParameterCategories,
        bindingKind: setupPlan.bindingKind,
      },
    };
  }

  if (setupPlan.action === 'add_family_parameters') {
    log(`Found ${setupPlan.matchingFamilies.length} existing ${targetObjectType} families`);
    const mutationTargets = setupPlan.targetFamiliesNeedingMutation;
    const familyIds = mutationTargets.map((f) => f.id);
    const familyNames = mutationTargets.map((f) => f.name);

    const result = await addParametersToBoltFamilies({
      familyIds,
      targetObjectType,
      parameterNames,
      bindingKind: setupPlan.bindingKind,
      parameterTypeHints,
      options,
    });

    if (result.success) {
      const modifiedFamiliesCount = result.modifiedFamiliesCount ?? mutationTargets.length;
      const resolvedFamilyTargetCount = result.resolvedFamilyTargetCount ?? mutationTargets.length;
      const unmodifiedFamilyTargetCount =
        result.unmodifiedFamilyTargetCount ??
        Math.max(0, resolvedFamilyTargetCount - modifiedFamiliesCount);
      log(`=== APPLY BOLT CONFIGURATION SUCCESS ===`);
      log(`Action: added_to_existing`);
      return {
        success: true,
        action: 'added_to_existing',
        message: buildFamilyParameterMutationMessage(
          targetObjectType,
          modifiedFamiliesCount,
          resolvedFamilyTargetCount,
          unmodifiedFamilyTargetCount
        ),
        targetObjectType,
        undoAvailable: true,
        details: {
          familyCount: mutationTargets.length,
          familyNames,
          parameterCount: parameterNames.length,
          bindingKind: setupPlan.bindingKind,
          modifiedFamiliesCount,
          resolvedFamilyTargetCount,
          processedFamilyTargetCount: result.processedFamilyTargetCount,
          openedFamilyCount: result.openedFamilyCount,
          unmodifiedFamilyTargetCount,
          existingFamilyParameterCount: result.existingFamilyParameterCount,
          conflictCount: result.conflictCount,
          failureCount: result.failureCount,
        },
      };
    }

    if (!isFamilyTargetResolutionFailure(result.error)) {
      log(`=== APPLY BOLT CONFIGURATION FAILED ===`);
      log(`Error:`, result.error);
      return {
        success: false,
        action: 'error',
        message: `Found ${targetObjectType} families but failed to add parameters`,
        targetObjectType,
        error: result.error,
        details: {
          familyCount: mutationTargets.length,
          familyNames,
        },
      };
    }

    // Sequential contract: presence check first — the family snapshot can be
    // stale, so when its ids do not resolve in the current project the flow
    // must not stop; it falls through to loading the reference family and
    // reconciles the selected parameters there.
    log(`Snapshot ${targetObjectType} targets did not resolve in the current project (${result.error}); loading the reference family instead`);
  }

  // The add_family_parameters plan variant carries no project categories;
  // the snapshot-fallback path must tolerate that union.
  const projectParameterCategories =
    'projectParameterCategories' in setupPlan ? setupPlan.projectParameterCategories : undefined;

  log(`No ${targetObjectType} families found in project - loading reference family`);
  const result = await loadReferenceFamilyAndReconcileParameters({
    targetObjectType,
    parameterNames,
    bindingKind: setupPlan.bindingKind,
    objectTypeParameterName: configInput.objectTypeParameterName,
    objectTypeBindingKind: configInput.objectTypeBindingKind,
    reconcileFamilyParameters: !projectParameterCategories,
    parameterTypeHints,
    expectedDocumentIdentity: options.expectedDocumentIdentity,
    onProgress: options.onProgress,
  });

  if (result.success) {
    log(`=== APPLY BOLT CONFIGURATION SUCCESS ===`);
    log(`Action: loaded_new_family (${result.familyName})`);
    if (projectParameterCategories) {
      const projectResult = await addProjectBoltParameters(
        parameterNames,
        projectParameterCategories,
        setupPlan.bindingKind,
        parameterTypeHints,
        options
      );

      if (!projectResult.success) {
        return {
          success: false,
          action: 'error',
          message: `Added reference ${targetObjectType} family but failed to add project parameters`,
          targetObjectType,
          error: projectResult.error,
          details: {
            loadedFamilyId: result.familyId,
            loadedFamilyName: result.familyName,
            parameterCount: parameterNames.length,
            projectParameterNames: parameterNames,
            projectParameterCategories,
            bindingKind: setupPlan.bindingKind,
            addedFamilyParameterNames: result.addedFamilyParameterNames,
            reboundFamilyParameters: result.reboundFamilyParameters,
            removedDefaultParameters: result.removedDefaultParameters,
          },
        };
      }

      return {
        success: true,
        action: 'added_project_parameters',
        message: `Added reference family "${result.familyName}" and ${parameterNames.length} bolt project parameters to ${projectParameterCategories.join(', ')}`,
        targetObjectType,
        details: {
          loadedFamilyId: result.familyId,
          loadedFamilyName: result.familyName,
          parameterCount: parameterNames.length,
          projectParameterNames: parameterNames,
          projectParameterCategories,
          bindingKind: setupPlan.bindingKind,
          addedFamilyParameterNames: result.addedFamilyParameterNames,
          reboundFamilyParameters: result.reboundFamilyParameters,
          removedDefaultParameters: result.removedDefaultParameters,
        },
        undoAvailable: true,
      };
    }

    return {
      success: true,
      action: 'loaded_new_family',
      message: `No ${targetObjectType} families found. Added reference family "${result.familyName}" with ${parameterNames.length} bolt parameters`,
      targetObjectType,
      details: {
        loadedFamilyId: result.familyId,
        loadedFamilyName: result.familyName,
        parameterCount: parameterNames.length,
        bindingKind: setupPlan.bindingKind,
        addedFamilyParameterNames: result.addedFamilyParameterNames,
        reboundFamilyParameters: result.reboundFamilyParameters,
        removedDefaultParameters: result.removedDefaultParameters,
      },
      undoAvailable: result.undoAvailable,
    };
  } else {
    log(`=== APPLY BOLT CONFIGURATION FAILED ===`);
    log(`Error:`, result.error);
    return {
      success: false,
      action: 'error',
      message: `No ${targetObjectType} families found and failed to load reference family`,
      targetObjectType,
      error: result.error,
    };
  }
}
